import React, { useEffect, useState, useRef } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Button from "../../common/Button";
import DialogBox from "../../common/DialogBox";
import apiService from "../../../services/ApiService";
import { apiRoutes } from "../../../shared/apiRoutes";
import { DocumentDuplicateIcon, KeyIcon } from "@heroicons/react/24/solid";

interface TenantFormValues {
  tenantName: string;
  userType: "Owner" | "Angadiya";
  mobileNo: string;
  remarks: string;
  password: string;
  confirmPassword: string;
}

interface CreateTenantSideDrawerDialogProps {
  onClose: () => void;
  isOpen?: boolean;
  editId?: number | null;
  onSuccess?: () => void;
}

const DUPLICATE_TENANT_NAME_MESSAGE = "A User or Tenant with this name already exists. Please, use different Tenant name.";

const isDuplicateNameError = (message: string) =>
  /already exist/i.test(message) && /(tenant|user)/i.test(message);

const showDuplicateNameOnField = (formikHelpers?: {
  setFieldError?: (field: string, message: string) => void;
  setFieldTouched?: (field: string, touched: boolean, shouldValidate?: boolean) => void;
}) => {
  formikHelpers?.setFieldTouched?.("tenantName", true, false);
  formikHelpers?.setFieldError?.("tenantName", DUPLICATE_TENANT_NAME_MESSAGE);
};

interface PendingTenantSave {
  payload: Record<string, unknown>;
  values: TenantFormValues;
  formikHelpers?: any;
  isEdit: boolean;
}

// Password validation schema - only required when creating (not editing)
const getValidationSchema = (isEdit: boolean) => {
  const baseSchema = {
    tenantName: Yup.string()
      .required("Tenant Name is required")
      .max(100, "Tenant Name must be at most 100 characters"),
    mobileNo: Yup.string()
      .trim()
      .test(
        "mobile-10",
        "Mobile No must be empty or a 10 digit number",
        (value) => !value || /^\d{10}$/.test(value),
      ),
    remarks: Yup.string()
      .max(500, "Remarks must be at most 500 characters")
      .optional(),
  };

  // Add password fields only for create mode
  if (!isEdit) {
    return Yup.object().shape({
      ...baseSchema,
      password: Yup.string()
        .required("Password is required")
        .min(8, "Password must be at least 8 characters")
        .max(100, "Password must be at most 100 characters")
        .matches(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]).{8,}$/,
          "Password must contain at least 1 lowercase, 1 uppercase, 1 number, and 1 special character"
        ),
      confirmPassword: Yup.string()
        .required("Confirm Password is required")
        .oneOf([Yup.ref('password')], "Passwords must match"),
      userType: Yup.string().oneOf(["Owner", "Angadiya"]).required("User type is required"),
    });
  }

  return Yup.object().shape(baseSchema);
};

const CreateTenantSideDrawerDialog: React.FC<CreateTenantSideDrawerDialogProps> = ({ 
  onClose, 
  isOpen = true, 
  editId = null, 
  onSuccess 
}) => {
  const [initialFormValues, setInitialFormValues] = useState<TenantFormValues | null>(null);
  const [loadingData, setLoadingData] = useState(false);
  const [generatedPassword, setGeneratedPassword] = useState<string | null>(null);
  const [showPasswordSuccess, setShowPasswordSuccess] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [pendingSave, setPendingSave] = useState<PendingTenantSave | null>(null);
  const [adminPassword, setAdminPassword] = useState("");
  const [adminPasswordError, setAdminPasswordError] = useState("");
  const [submittingAdminPassword, setSubmittingAdminPassword] = useState(false);
  
  const tenantNameInputRef = useRef<HTMLInputElement>(null);
  const mobileNoInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const confirmPasswordInputRef = useRef<HTMLInputElement>(null);
  const remarksInputRef = useRef<HTMLTextAreaElement>(null);

  // Function to move focus to next field
  const moveToNextField = (currentRef: React.RefObject<HTMLInputElement | HTMLTextAreaElement | null>, nextRef: React.RefObject<HTMLInputElement | HTMLTextAreaElement | null>) => {
    if (nextRef.current) {
      setTimeout(() => {
        nextRef.current?.focus();
      }, 0);
    }
  };

  // Function to generate a secure random password
  const generatePassword = (): string => {
    const lowercase = 'abcdefghijklmnopqrstuvwxyz';
    const uppercase = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    const special = '!@#$%^&*()_+-=[]{}|;:,.<>?';
    const allChars = lowercase + uppercase + numbers + special;
    
    // Ensure at least one of each required character type
    let password = '';
    password += lowercase[Math.floor(Math.random() * lowercase.length)];
    password += uppercase[Math.floor(Math.random() * uppercase.length)];
    password += numbers[Math.floor(Math.random() * numbers.length)];
    password += special[Math.floor(Math.random() * special.length)];
    
    // Fill the rest randomly to make it 12 characters total
    for (let i = password.length; i < 12; i++) {
      password += allChars[Math.floor(Math.random() * allChars.length)];
    }
    
    // Shuffle the password
    return password.split('').sort(() => Math.random() - 0.5).join('');
  };

  // Function to copy password to clipboard
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      window.dispatchEvent(new CustomEvent('show-toast', {
        detail: { message: 'Password copied to clipboard', type: 'success' }
      }));
    } catch (err) {
      console.error('Failed to copy password:', err);
      window.dispatchEvent(new CustomEvent('show-toast', {
        detail: { message: 'Failed to copy password', type: 'error' }
      }));
    }
  };

  // Function to move focus to next field by name
  const moveToNextFieldByName = (currentFieldName: string, nextFieldName: string) => {
    const fieldMap: Record<string, React.RefObject<HTMLInputElement | HTMLTextAreaElement | null>> = {
      tenantName: tenantNameInputRef,
      mobileNo: mobileNoInputRef,
      password: passwordInputRef,
      confirmPassword: confirmPasswordInputRef,
      remarks: remarksInputRef,
    };
    
    const nextRef = fieldMap[nextFieldName];
    if (nextRef) {
      setTimeout(() => {
        nextRef.current?.focus();
      }, 0);
    }
  };

  // Fetch tenant data when editing
  useEffect(() => {
    if (isOpen && editId) {
      fetchTenantData();
      setGeneratedPassword(null);
      setShowPasswordSuccess(false);
    } else if (isOpen && !editId) {
      // Reset form for new tenant
      setInitialFormValues({
        tenantName: "",
        userType: "Owner",
        mobileNo: "",
        remarks: "",
        password: "",
        confirmPassword: "",
      });
      setGeneratedPassword(null);
      setShowPasswordSuccess(false);
    }
  }, [isOpen, editId]);

  const fetchTenantData = async () => {
    if (!editId) return;
    
    setLoadingData(true);
    try {
      const response = await apiService.get<any>(apiRoutes.tenants.getById(editId));
      const data = response.data || response;
      
      // Set form values - handle different field name variations
      // Note: Password fields are not included when editing
      const formValues: TenantFormValues = {
        tenantName: data.tenantName || data.tenant_name || data.name || '',
        userType: "Owner",
        mobileNo: data.mobileNo || data.mobile_no || data.mobile || data.phoneNumber || data.phone_number || data.phone || '',
        remarks: data.remarks || data.remark || data.notes || '',
        password: '', // Not used in edit mode
        confirmPassword: '', // Not used in edit mode
      };
      
      setInitialFormValues(formValues);
    } catch (error) {
      console.error('Error fetching tenant data:', error);
      window.dispatchEvent(new CustomEvent('show-toast', {
        detail: { message: 'Failed to load tenant data', type: 'error' }
      }));
      setInitialFormValues({
        tenantName: "",
        userType: "Owner",
        mobileNo: "",
        remarks: "",
        password: "",
        confirmPassword: "",
      });
    } finally {
      setLoadingData(false);
    }
  };

  const submitCreateTenant = async (
    payload: any,
    values: TenantFormValues,
    formikHelpers?: any,
  ) => {
    // Create new tenant - API returns { success, message, tenantId, databaseName }
    const response = await apiService.post<{
      success: boolean;
      message: string;
      tenantId: number | null;
      databaseName: string | null;
    }>(apiRoutes.tenants.create, payload);

    // Check response for success
    if (response.success) {
      const successMessage = response.message || `Tenant '${values.tenantName}' created successfully`;
      
      // If password was auto-generated, show it to user
      if (generatedPassword) {
        setShowPasswordSuccess(true);
        // Don't close the drawer yet - user needs to copy password
        // Don't call onSuccess yet - wait for user to close manually
      } else {
        // Normal success flow
        if (response.databaseName) {
          window.dispatchEvent(new CustomEvent('show-toast', {
            detail: { message: `${successMessage} with database '${response.databaseName}'`, type: 'success' }
          }));
        } else {
          window.dispatchEvent(new CustomEvent('show-toast', {
            detail: { message: successMessage, type: 'success' }
          }));
        }
        
        // Call onSuccess callback to refresh list
        if (onSuccess) {
          onSuccess();
        }
        
        // Dispatch custom event to refresh tenant list
        window.dispatchEvent(new CustomEvent('tenant-changed', {
          detail: { editId, isEdit: !!editId }
        }));
        
        // Close the drawer
        onClose();
      }
      return true;
    }

    // API returned success: false
    const errorMessage = response.message || 'Failed to create tenant';
    if (isDuplicateNameError(errorMessage)) {
      showDuplicateNameOnField(formikHelpers);
    } else {
      window.dispatchEvent(new CustomEvent('show-toast', {
        detail: { message: errorMessage, type: 'error' }
      }));
    }
    
    if (formikHelpers?.setSubmitting) {
      formikHelpers.setSubmitting(false);
    }
    return false;
  };

  const closeAdminPasswordDialog = () => {
    if (submittingAdminPassword) return;
    setPendingSave(null);
    setAdminPassword("");
    setAdminPasswordError("");
  };

  const isPasswordAuthFailure = (error: unknown): boolean => {
    const err = error as { status?: number; message?: string; error?: string };
    const msg = `${err?.message ?? ''} ${err?.error ?? ''}`;
    return err?.status === 401 || /invalid admin password|incorrect password|wrong password|invalid password/i.test(msg);
  };

  const confirmAdminPasswordAndSave = async () => {
    if (!pendingSave) return;
    const trimmedAdminPassword = adminPassword.trim();
    if (!trimmedAdminPassword) {
      setAdminPasswordError("Your login password is required.");
      return;
    }

    setSubmittingAdminPassword(true);
    try {
      const payload = {
        ...pendingSave.payload,
        adminPassword: trimmedAdminPassword,
        password: trimmedAdminPassword,
      };
      if (pendingSave.isEdit && editId) {
        await apiService.put(apiRoutes.tenants.update(editId), payload);
        window.dispatchEvent(new CustomEvent('show-toast', {
          detail: { message: 'Tenant updated successfully', type: 'success' }
        }));
        if (onSuccess) onSuccess();
        window.dispatchEvent(new CustomEvent('tenant-changed', {
          detail: { editId, isEdit: true }
        }));
        setPendingSave(null);
        setAdminPassword("");
        setAdminPasswordError("");
        onClose();
      } else {
        await submitCreateTenant(
          {
            ...pendingSave.payload,
            AdminPassword: trimmedAdminPassword,
            adminPassword: trimmedAdminPassword,
          },
          pendingSave.values,
          pendingSave.formikHelpers,
        );
        setPendingSave(null);
        setAdminPassword("");
        setAdminPasswordError("");
      }
    } catch (error: any) {
      console.error('Error saving tenant:', error);
      const errorMessage = error?.message || error?.error || 'Failed to save tenant';
      if (isPasswordAuthFailure(error) || /incorrect password/i.test(errorMessage)) {
        setAdminPasswordError('Incorrect password');
      } else if (error?.status === 403) {
        setAdminPasswordError('You do not have permission to perform this action.');
      } else if (isDuplicateNameError(errorMessage)) {
        showDuplicateNameOnField(pendingSave.formikHelpers);
        setPendingSave(null);
        setAdminPassword("");
        setAdminPasswordError("");
      } else {
        setPendingSave(null);
        setAdminPassword("");
        setAdminPasswordError("");
        window.dispatchEvent(new CustomEvent('show-toast', {
          detail: { message: errorMessage, type: 'error' }
        }));
      }
    } finally {
      setSubmittingAdminPassword(false);
      if (pendingSave.formikHelpers?.setSubmitting) {
        pendingSave.formikHelpers.setSubmitting(false);
      }
    }
  };

  const handleSubmit = async (values: TenantFormValues, formikHelpers?: any) => {
    try {
      // Prepare payload - only include fields that have values
      const payload: any = {
        tenantName: values.tenantName.trim(),
      };

      // Add optional fields only if they have values
      if (values.mobileNo && values.mobileNo.trim()) {
        payload.mobileNo = values.mobileNo.trim();
      }
      if (values.remarks && values.remarks.trim()) {
        payload.remarks = values.remarks.trim();
      }

      // Add password only when creating (not editing)
      if (!editId && values.password) {
        payload.password = values.password;
        payload.isAngadiya = values.userType === "Angadiya";
      }

      // Call the appropriate API endpoint after password confirmation
      setPendingSave({ payload, values, formikHelpers, isEdit: Boolean(editId) });
      setAdminPassword("");
      setAdminPasswordError("");
      if (formikHelpers?.setSubmitting) {
        formikHelpers.setSubmitting(false);
      }
      return false;
    } catch (error: any) {
      console.error('Error saving tenant:', error);
      
      // Handle error response format { success: false, message: "...", tenantId: null, databaseName: null }
      let errorMessage = 'Failed to create tenant';
      
      if (error?.response?.data) {
        const errorData = error.response.data;
        if (errorData.message) {
          errorMessage = errorData.message;
        } else if (errorData.success === false && errorData.message) {
          errorMessage = errorData.message;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      } else if (error?.error) {
        errorMessage = error.error;
      }
      
      window.dispatchEvent(new CustomEvent('show-toast', {
        detail: { message: errorMessage, type: 'error' }
      }));
      
      // Set submitting to false if formikHelpers provided
      if (formikHelpers?.setSubmitting) {
        formikHelpers.setSubmitting(false);
      }

      // Return failure status
      return false;
    }
  };

  // Default initial values
  const defaultInitialValues: TenantFormValues = {
    tenantName: "",
    userType: "Owner",
    mobileNo: "",
    remarks: "",
    password: "",
    confirmPassword: "",
  };

  // Use initialFormValues if available (for edit mode), otherwise use defaults
  const formInitialValues = initialFormValues || defaultInitialValues;

  // Don't render form until data is loaded (for edit mode)
  if (editId && loadingData) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  // Handle auto-generate password
  const handleAutoGeneratePassword = (setFieldValue: any) => {
    const newPassword = generatePassword();
    setGeneratedPassword(newPassword);
    setFieldValue('password', newPassword);
    setFieldValue('confirmPassword', newPassword);
    // Focus on password field after generation
    setTimeout(() => {
      passwordInputRef.current?.focus();
    }, 0);
  };

  // Handle close when password was auto-generated
  const handleCloseWithPassword = () => {
    if (onSuccess) {
      onSuccess();
    }
    window.dispatchEvent(new CustomEvent('tenant-changed', {
      detail: { editId, isEdit: !!editId }
    }));
    setShowPasswordSuccess(false);
    setGeneratedPassword(null);
    onClose();
  };

  return (
    <>
    <Formik
      key={editId || 'new'} // Force reinitialize when editId changes
      initialValues={formInitialValues}
      enableReinitialize={true}
      validationSchema={getValidationSchema(!!editId)}
      onSubmit={handleSubmit}
    >
      {({ isSubmitting, values, setFieldValue, submitForm }) => (
        <Form className="space-y-4">
          {/* Tenant Name */}
          <div>
            <label className="block font-medium text-sm text-gray-700 mb-1">
              Tenant Name<span className="text-red-500">*</span>
            </label>
            <Field name="tenantName">
              {({ field }: any) => (
                <input
                  {...field}
                  ref={tenantNameInputRef}
                  type="text"
                  className="w-full p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Enter tenant name"
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      moveToNextFieldByName('tenantName', 'mobileNo');
                    }
                  }}
                />
              )}
            </Field>
            <ErrorMessage name="tenantName">
              {(msg) => (
                <div
                  className={`text-red-500 mt-1 ${
                    msg === DUPLICATE_TENANT_NAME_MESSAGE ? "text-sm font-bold" : "text-xs"
                  }`}
                >
                  {msg}
                </div>
              )}
            </ErrorMessage>
          </div>

          {!editId && (
            <div>
              <label className="block font-medium text-sm text-gray-700 mb-1">
                User type<span className="text-red-500">*</span>
              </label>
              <Field
                as="select"
                name="userType"
                className="w-full p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
              >
                <option value="Owner">Owner</option>
                <option value="Angadiya">Angadiya</option>
              </Field>
              <p className="text-xs text-gray-500 mt-1">
                Angadiya creates the tenant login as an Angadiya owner.
              </p>
              <ErrorMessage
                name="userType"
                component="div"
                className="text-red-500 text-xs mt-1"
              />
            </div>
          )}

          {/* Mobile No */}
          <div>
            <label className="block font-medium text-sm text-gray-700 mb-1">
              Mobile No
            </label>
            <Field name="mobileNo">
              {({ field }: any) => (
                <input
                  {...field}
                  ref={mobileNoInputRef}
                  type="text"
                  className="w-full p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="10 digit number, or leave blank"
                  inputMode="numeric"
                  maxLength={10}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const digits = e.target.value.replace(/\D/g, "").slice(0, 10);
                    field.onChange({ target: { name: "mobileNo", value: digits } });
                  }}
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      moveToNextFieldByName('mobileNo', 'remarks');
                    }
                  }}
                />
              )}
            </Field>
            <ErrorMessage
              name="mobileNo"
              component="div"
              className="text-red-500 text-xs mt-1"
            />
          </div>

          {/* Password Fields - Only show when creating (not editing) */}
          {!editId && (
            <>
              {/* Password */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block font-medium text-sm text-gray-700">
                    Password<span className="text-red-500">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={() => handleAutoGeneratePassword(setFieldValue)}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                  >
                    <KeyIcon className="w-4 h-4" />
                    Auto Generate
                  </button>
                </div>
                <div className="relative">
                  <Field name="password">
                    {({ field }: any) => (
                      <input
                        {...field}
                        ref={passwordInputRef}
                        type={showPassword ? "text" : "password"}
                        className="w-full p-2 pr-8 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Enter password (min 8 chars with complexity)"
                        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            moveToNextFieldByName('password', 'confirmPassword');
                          }
                        }}
                      />
                    )}
                  </Field>
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 text-xs"
                  >
                    {showPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
                <ErrorMessage
                  name="password"
                  component="div"
                  className="text-red-500 text-xs mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Must contain: 1 lowercase, 1 uppercase, 1 number, 1 special character
                </p>
              </div>

              {/* Confirm Password */}
              <div>
                <div className="relative">
                  <label className="block font-medium text-sm text-gray-700 mb-1">
                    Confirm Password<span className="text-red-500">*</span>
                  </label>
                  <Field name="confirmPassword">
                    {({ field }: any) => (
                      <input
                        {...field}
                        ref={confirmPasswordInputRef}
                        type={showConfirmPassword ? "text" : "password"}
                        className="w-full p-2 pr-8 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Confirm password"
                        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            moveToNextFieldByName('confirmPassword', 'remarks');
                          }
                        }}
                      />
                    )}
                  </Field>
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-2 top-8 text-gray-500 hover:text-gray-700 text-xs"
                  >
                    {showConfirmPassword ? 'Hide' : 'Show'}
                  </button>
                </div>
                <ErrorMessage
                  name="confirmPassword"
                  component="div"
                  className="text-red-500 text-xs mt-1"
                />
              </div>
            </>
          )}

          {/* Remarks */}
          <div>
            <label className="block font-medium text-sm text-gray-700 mb-1">
              Remarks
            </label>
            <Field name="remarks">
              {({ field }: any) => (
                <textarea
                  {...field}
                  ref={remarksInputRef}
                  rows={4}
                  className="w-full p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  placeholder="Enter remarks (optional)"
                  onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                    if (e.key === 'Enter' && e.ctrlKey) {
                      e.preventDefault();
                      submitForm();
                    }
                  }}
                />
              )}
            </Field>
            <ErrorMessage
              name="remarks"
              component="div"
              className="text-red-500 text-xs mt-1"
            />
          </div>

          {/* Password Success Display - Show after successful creation with auto-generated password */}
          {showPasswordSuccess && generatedPassword && (
            <div className="bg-green-50 border border-green-200 rounded-md p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-sm font-semibold text-green-800 mb-1">Tenant Created Successfully!</h4>
                  <p className="text-xs text-green-700">Please copy the password before closing:</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  readOnly
                  value={generatedPassword}
                  className="flex-1 p-2 text-sm font-mono bg-white border border-green-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                />
                <button
                  type="button"
                  onClick={() => copyToClipboard(generatedPassword)}
                  className="flex items-center gap-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm rounded-md transition-colors"
                  title="Copy password"
                >
                  <DocumentDuplicateIcon className="w-4 h-4" />
                  Copy
                </button>
              </div>
              <p className="text-xs text-green-600 italic">⚠️ This password will not be shown again. Please save it securely.</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-2 pt-4 border-t border-gray-200">
            {showPasswordSuccess ? (
              <Button
                type="button"
                onClick={handleCloseWithPassword}
                className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md text-sm"
              >
                Done
              </Button>
            ) : (
              <>
                <Button
                  type="button"
                  onClick={onClose}
                  className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded-md text-sm"
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  isProcessing={isSubmitting}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {editId ? 'Update Tenant' : 'Create Tenant'}
                </Button>
              </>
            )}
          </div>
        </Form>
      )}
    </Formik>
    <DialogBox
      isOpen={pendingSave !== null}
      onClose={closeAdminPasswordDialog}
      title="Confirm with your password"
      panelClassName="w-[min(100vw-2rem,24rem)]"
    >
      <div className="p-4 space-y-3">
        <p className="text-sm text-gray-700">
          {pendingSave?.isEdit
            ? 'Enter your login password to save these tenant changes.'
            : 'Enter your login password to create this tenant.'}
        </p>
        <div>
          <label htmlFor="tenant-admin-password" className="block text-sm font-medium text-gray-700 mb-1">
            Your password
          </label>
          <input
            id="tenant-admin-password"
            type="password"
            value={adminPassword}
            onChange={(e) => {
              setAdminPassword(e.target.value);
              setAdminPasswordError("");
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                confirmAdminPasswordAndSave();
              }
            }}
            autoFocus
            autoComplete="current-password"
            disabled={submittingAdminPassword}
            className="w-full p-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Your login password"
          />
          {adminPasswordError ? (
            <p className="text-xs text-red-600 mt-1">{adminPasswordError}</p>
          ) : null}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button
            type="button"
            onClick={closeAdminPasswordDialog}
            disabled={submittingAdminPassword}
            className="bg-gray-400 hover:bg-gray-500 text-white px-4 py-2 rounded-md text-sm"
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={confirmAdminPasswordAndSave}
            isProcessing={submittingAdminPassword}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md text-sm"
          >
            {pendingSave?.isEdit ? 'Save Tenant' : 'Create Tenant'}
          </Button>
        </div>
      </div>
    </DialogBox>
    </>
  );
};

export default CreateTenantSideDrawerDialog;
